/**
 * TanStack Query hooks for Data Classification Management
 * Provides classification CRUD operations, tree view, and auto-classification
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { get, post, put, del } from '@/lib/api-client'
import { isApiError } from '@/lib/api-client'
import { useAuthStore } from '@/stores/auth-store'
import type {
  DataClassification,
  ClassificationNode,
  CreateClassificationRequest,
  UpdateClassificationRequest,
  ClassificationSuggestion,
} from '@/types/classification'

/**
 * Query keys for classification management
 */
export const classificationKeys = {
  all: ['classifications'] as const,
  tree: () => [...classificationKeys.all, 'tree'] as const,
  details: () => [...classificationKeys.all, 'detail'] as const,
  detail: (id: string) => [...classificationKeys.details(), id] as const,
  autoClassify: (columnId: string) =>
    [...classificationKeys.all, 'auto-classify', columnId] as const,
}

// ============================================================================
// QUERY HOOKS
// ============================================================================

/**
 * Fetch classification tree
 *
 * Returns hierarchical tree structure of all classifications.
 * Useful for displaying classification hierarchy in UI.
 *
 * @returns Query result with classification tree
 *
 * @example
 * ```tsx
 * const { data: tree, isLoading } = useClassificationTree()
 * // tree is an array of root nodes, each with nested children
 * ```
 */
export function useClassificationTree() {
  const { user } = useAuthStore()
  const tenantId = user?.tenantId ?? ''

  return useQuery({
    queryKey: classificationKeys.tree(),
    queryFn: () => get<ClassificationNode[]>('/classifications/tree'),
    enabled: !!tenantId,
    // Classifications don't change often, cache for 10 minutes
    staleTime: 10 * 60 * 1000,
  })
}

/**
 * Fetch single classification by ID
 *
 * @param id - Classification ID
 * @returns Query result with classification details
 *
 * @example
 * ```tsx
 * const { data: classification } = useClassification('class-id-123')
 * ```
 */
export function useClassification(id: string) {
  return useQuery({
    queryKey: classificationKeys.detail(id),
    queryFn: () => get<DataClassification>(`/classifications/${id}`),
    enabled: !!id,
    staleTime: 10 * 60 * 1000,
  })
}

/**
 * Get auto-classification suggestions for a column
 *
 * @param columnId - Data column ID
 * @returns Query result with classification suggestions
 *
 * @example
 * ```tsx
 * const { data: suggestions } = useAutoClassify('column-id-123')
 * // suggestions is an array sorted by confidence (highest first)
 * ```
 */
export function useAutoClassify(columnId: string) {
  return useQuery({
    queryKey: classificationKeys.autoClassify(columnId),
    queryFn: () =>
      post<ClassificationSuggestion[]>('/classifications/auto-classify', {
        columnId,
      }),
    enabled: !!columnId,
    // Don't cache auto-classify results
    staleTime: 0,
  })
}

// ============================================================================
// MUTATION HOOKS
// ============================================================================

/**
 * Create new classification
 *
 * @returns Mutation object with mutate function
 *
 * @example
 * ```tsx
 * const createClassification = useCreateClassification()
 * createClassification.mutate({
 *   name: 'PII',
 *   displayName: 'Personally Identifiable Information',
 *   level: 'CONFIDENTIAL',
 *   regulatoryFramework: ['GDPR', 'CCPA']
 * })
 * ```
 */
export function useCreateClassification() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateClassificationRequest) =>
      post<DataClassification>('/classifications', data),
    onSuccess: () => {
      // Invalidate tree to refetch with new classification
      queryClient.invalidateQueries({ queryKey: classificationKeys.tree() })
      toast.success('Classification created successfully')
    },
    onError: (error) => {
      const message = isApiError(error)
        ? error.message
        : 'Failed to create classification'
      toast.error(message)
    },
  })
}

/**
 * Update existing classification
 *
 * @returns Mutation object with mutate function
 *
 * @example
 * ```tsx
 * const updateClassification = useUpdateClassification()
 * updateClassification.mutate({
 *   id: 'class-id-123',
 *   data: { displayName: 'Updated Name' }
 * })
 * ```
 */
export function useUpdateClassification() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: UpdateClassificationRequest
    }) => put<DataClassification>(`/classifications/${id}`, data),
    onSuccess: (classification) => {
      // Invalidate tree
      queryClient.invalidateQueries({ queryKey: classificationKeys.tree() })
      // Update detail cache
      queryClient.setQueryData(
        classificationKeys.detail(classification.id),
        classification
      )
      toast.success('Classification updated successfully')
    },
    onError: (error) => {
      const message = isApiError(error)
        ? error.message
        : 'Failed to update classification'
      toast.error(message)
    },
  })
}

/**
 * Delete classification
 *
 * @returns Mutation object with mutate function
 *
 * @example
 * ```tsx
 * const deleteClassification = useDeleteClassification()
 * deleteClassification.mutate('class-id-123')
 * ```
 */
export function useDeleteClassification() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => del(`/classifications/${id}`),
    onSuccess: () => {
      // Invalidate tree to remove deleted classification
      queryClient.invalidateQueries({ queryKey: classificationKeys.tree() })
      toast.success('Classification deleted successfully')
    },
    onError: (error) => {
      const message = isApiError(error)
        ? error.message
        : 'Failed to delete classification. It may be in use by data tables or columns.'
      toast.error(message)
    },
  })
}
